import { useMutation } from '@apollo/client';
import type { InsertCustomDataSourceMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertCustomDataSourceDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { InsertCustomDatasourceTRPCInput } from './useInsertCustomDatasourceTRPC';
import { useInsertCustomDatasourceTRPC } from './useInsertCustomDatasourceTRPC';

export const useInsertCustomDatasource = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation — evict the custom_datasource query families so the
  // cache-first reads (list + `useGetCustomDatasourceById`) refetch after a
  // create, keeping parity with the delete hook and the tRPC twin (RSP-6650).
  const [insertCustomDatasourceGraphQL, graphqlState] = useMutation(
    InsertCustomDataSourceDocument,
    {
      update: (cache) => {
        evictField(cache, 'custom_datasource');
        evictField(cache, 'custom_datasource_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useInsertCustomDatasourceTRPC();

  const insertCustomDatasource = async (
    variables: InsertCustomDatasourceTRPCInput
  ): Promise<InsertCustomDataSourceMutation> => {
    if (trpcEnabled) {
      return trpcMutation.insertCustomDatasource(variables);
    }

    const result = await insertCustomDatasourceGraphQL({
      variables: {
        customDatasource: {
          Title: variables.Title,
          Datasources: variables.Datasources,
          Fields: variables.Fields,
          Filters: variables.Filters,
        },
      },
    });
    if (!result.data) {
      throw new Error('Failed to insert custom datasource');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      insertCustomDatasource,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    insertCustomDatasource,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
